import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {devtools} from 'zustand/middleware';
import {LivenessAction} from 'components/LivenessDetection/types';
import {
  IMAGE_SERVICE_ORIGIN,
  IV_DOCUMENT_IMAGE_TYPES,
  type DocumentChoice,
  type ImageDetails,
} from '@guestapp/sdk';

export const DEFAULT_IMAGE_DETAILS = {
  [IV_DOCUMENT_IMAGE_TYPES.front]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    type: IV_DOCUMENT_IMAGE_TYPES.front,
  },
  [IV_DOCUMENT_IMAGE_TYPES.back]: {
    id: '',
    image: null,
    uploaded: false,
    mrzDetected: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.ocr,
    type: IV_DOCUMENT_IMAGE_TYPES.back,
  },
  [IV_DOCUMENT_IMAGE_TYPES.selfie]: {
    id: '',
    image: null,
    uploaded: false,
    faceDetected: false,
    service: IMAGE_SERVICE_ORIGIN.biomatch,
    type: IV_DOCUMENT_IMAGE_TYPES.selfie,
  },
};

export type IdentityVerificationSchema = {
  ivIntentId?: string;
  isRemoteSession: boolean;
  setupFormDate: {
    documentType?: DocumentChoice;
    nationality?: string;
  };
  imagesDetails: {
    [key in IV_DOCUMENT_IMAGE_TYPES]: Partial<ImageDetails> & {
      type: IV_DOCUMENT_IMAGE_TYPES;
    };
  };
  ocrResultData: {
    ocrPassed: boolean;
  };
  biomatchResultData: {
    biomatchPassed: boolean;
    distance: number | null;
  };
  livenessResultData?: {
    livenessPassed?: boolean;
    completedActions?: LivenessAction[];
    livenessVideoUrl?: string;
    performedBy?: 'CLIENT' | 'SERVER';
  };
  isIvFailed: boolean;
};

export const initialState: IdentityVerificationSchema = {
  ivIntentId: undefined,
  isRemoteSession: false,
  setupFormDate: {},
  imagesDetails: DEFAULT_IMAGE_DETAILS,
  ocrResultData: {
    ocrPassed: false,
  },
  biomatchResultData: {
    biomatchPassed: false,
    distance: 0,
  },
  isIvFailed: false,
};

type Actions = {
  setDocumentType: (documentType: DocumentChoice) => void;
  setNationality: (nationality: string) => void;
  setOCRDocumentPassed: (ocrPassed: boolean) => void;
  setBiomatchPassed: (biomatchPassed: boolean) => void;
  setDistanceBiomatchComparing: (distance: number) => void;
  setImageDetails: (
    payload: Partial<ImageDetails> & {type: IV_DOCUMENT_IMAGE_TYPES},
  ) => void;
  resetIVConfigState: () => void;
  resetIVSetupFormDate: () => void;
  resetAllIVState: () => void;
  sync: (identityVerificationSchema: Partial<IdentityVerificationSchema>) => void;
  setRemoteSession: (flag: boolean) => void;
  setLivenessResultData: (
    livenessResultData: Partial<IdentityVerificationSchema['livenessResultData']>,
  ) => void;
  setIsIvFailed: (isIvFailed: boolean) => void;
};

export const useIVStore = create<IdentityVerificationSchema & Actions>()(
  devtools(
    immer(set => ({
      ...initialState,
      setDocumentType: documentType =>
        set(state => {
          state.setupFormDate.documentType = documentType;
        }),
      setNationality: nationality =>
        set(state => {
          state.setupFormDate.nationality = nationality;
        }),
      setOCRDocumentPassed: ocrPassed =>
        set(state => {
          state.ocrResultData.ocrPassed = ocrPassed;
        }),
      setBiomatchPassed: biomatchPassed =>
        set(state => {
          state.biomatchResultData.biomatchPassed = biomatchPassed;
        }),
      setDistanceBiomatchComparing: distance =>
        set(state => {
          state.biomatchResultData.distance = distance;
        }),
      setImageDetails: ({type, ...imageDetails}) =>
        set(state => {
          state.imagesDetails[type] = {
            ...state.imagesDetails[type],
            ...imageDetails,
            uploaded: Boolean(imageDetails.image),
          };
        }),
      resetIVConfigState: () =>
        set(state => {
          state.biomatchResultData = initialState.biomatchResultData;
          state.imagesDetails = initialState.imagesDetails;
          state.ocrResultData = initialState.ocrResultData;
        }),
      resetIVSetupFormDate: () =>
        set(state => {
          state.setupFormDate = initialState.setupFormDate;
        }),
      resetAllIVState: () => set(() => initialState, false),
      sync: identityVerificationSchema => set(() => identityVerificationSchema),
      setRemoteSession: flag =>
        set(state => {
          state.isRemoteSession = flag;
        }),
      setLivenessResultData: livenessResultData =>
        set(state => {
          state.livenessResultData = {
            ...state.livenessResultData,
            ...livenessResultData,
          };
        }),
      setIsIvFailed: isIvFailed =>
        set(state => {
          state.isIvFailed = isIvFailed;
        }),
    })),
  ),
);
